import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';

export type LiveState = 'connecting' | 'live' | 'offline';

/**
 * Live updates (plan 4.5, no 30-second polling): the server says "your work changed" and the
 * app reloads the day. After a lost connection the day is reloaded too, in case a change was missed.
 */
export function useLiveUpdates(): LiveState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveState>('connecting');

  useEffect(() => {
    const socket = io({ path: '/api/socket.io', withCredentials: true });
    const reload = () => void queryClient.invalidateQueries({ queryKey: ['courier'] });
    let wasOffline = false;
    socket.on('connect', () => {
      setState('live');
      if (wasOffline) reload();
    });
    socket.on('disconnect', () => {
      wasOffline = true;
      setState('offline');
    });
    socket.on('connect_error', () => {
      wasOffline = true;
      setState('offline');
    });
    socket.on('work.changed', reload);
    // the server closed the connection: the session has ended, ask the API (it sends the courier to sign in)
    socket.on('signed.out', () => void queryClient.invalidateQueries({ queryKey: ['auth'] }));
    return () => {
      socket.disconnect();
    };
  }, [queryClient]);

  return state;
}
